import { useState, useEffect, useCallback } from "react";
import { Inbox, Loader2, Trash2, Globe, Building2, Phone, Mail, Car, MessageSquare, ChevronDown, UserPlus, Sparkles, Copy } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const STATUSES = [
  { key: "new", label: "Nová", cls: "bg-orange-500/15 text-orange-400 border-orange-500/25" },
  { key: "contacted", label: "Voláno", cls: "bg-sky-500/15 text-sky-400 border-sky-500/25" },
  { key: "won", label: "Uzavřeno", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" },
  { key: "lost", label: "Ztraceno", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/25" },
];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "");

const LeadCard = ({ lead, token, onChange }) => {
  const [note, setNote] = useState(lead.note || "");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const draftReply = async () => {
    setAiLoading(true);
    try {
      const ctx = `Jméno: ${lead.name || "—"}. Vozidlo: ${lead.vehicle || "—"}${lead.count ? ` (${lead.count} ks)` : ""}. Délka pronájmu: ${lead.duration || "—"}. Zpráva zákazníka: ${lead.message || "(bez zprávy)"}`;
      const r = await apiFetch("/admin/ai/assist", { method: "POST", token, body: { mode: "reply", text: ctx } });
      setAiText(r.result || "");
    } catch (e) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  const patch = async (body) => {
    setSaving(true);
    try {
      await apiFetch(`/admin/leads/${lead.source}/${lead.id}`, { method: "PATCH", token, body });
      if (body.status) { toast.success("Stav aktualizován."); onChange(); }
      else toast.success("Poznámka uložena.");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog({ title: "Smazat poptávku?", confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/leads/${lead.source}/${lead.id}`, { method: "DELETE", token }); toast.success("Smazáno."); onChange(); }
    catch (e) { toast.error(e.message); }
  };

  const convert = async () => {
    if (!(await confirmDialog({ title: "Převést na klienta?", description: `Z poptávky „${lead.name || ""}" vznikne klient. Poté mu můžete přidat vůz a smlouvu v záložce Klienti.`, confirmText: "Převést" }))) return;
    setSaving(true);
    try {
      const r = await apiFetch(`/admin/leads/${lead.source}/${lead.id}/convert`, { method: "POST", token });
      toast.success(r.existing ? "Klient už existoval – údaje doplněny." : "Klient vytvořen. Najdete ho v záložce Klienti.");
      onChange();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-xl p-4" data-testid={`lead-${lead.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${lead.source === "web" ? "border-indigo-500/30 text-indigo-300" : "border-orange-500/30 text-orange-300"}`}>
              {lead.source === "web" ? <Globe className="w-2.5 h-2.5" /> : <Building2 className="w-2.5 h-2.5" />}
              {lead.source === "web" ? "Web" : "Spolupráce"}
            </span>
            {(lead.message || "").startsWith("[AI CHAT]") && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-orange-400/40 text-orange-300 bg-orange-500/10" data-testid={`lead-aichat-badge-${lead.id}`}>
                <Sparkles className="w-2.5 h-2.5" /> AI chat
              </span>
            )}
            <span className="text-xs text-zinc-500">{fmtDate(lead.created_at)}</span>
          </div>
          <p className="font-semibold mt-1 truncate">{lead.name || "—"}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400 mt-1">
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /><a href={`tel:${lead.phone}`} className="hover:text-white">{lead.phone}</a></span>}
            {lead.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /><a href={`mailto:${lead.email}`} className="hover:text-white">{lead.email}</a></span>}
            {lead.vehicle && <span className="inline-flex items-center gap-1"><Car className="w-3 h-3" />{lead.vehicle}{lead.count ? ` (${lead.count})` : ""}{lead.duration ? ` · ${lead.duration}` : ""}</span>}
          </div>
        </div>
        <button onClick={remove} className="text-zinc-600 hover:text-red-400 p-1 shrink-0" data-testid={`lead-delete-${lead.id}`} aria-label="Smazat"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => patch({ status: s.key })} disabled={saving}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${lead.status === s.key ? s.cls : "border-white/10 text-zinc-500 hover:text-white"}`}
            data-testid={`lead-status-${lead.id}-${s.key}`}>
            {s.label}
          </button>
        ))}
        <button onClick={() => setOpen(!open)} className="ml-auto text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid={`lead-toggle-${lead.id}`}>
          <MessageSquare className="w-3 h-3" /> Poznámka <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button onClick={convert} disabled={saving} className="text-[11px] px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`lead-convert-${lead.id}`}>
          <UserPlus className="w-3 h-3" /> Převést na klienta
        </button>
        <button onClick={draftReply} disabled={aiLoading} className="text-[11px] px-2.5 py-1 rounded-full border border-orange-500/30 text-orange-300 hover:bg-orange-500/10 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`lead-ai-${lead.id}`}>
          {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI návrh odpovědi
        </button>
      </div>

      {(lead.message || open) && (
        <div className="mt-3 space-y-2">
          {lead.message && <p className="text-sm text-zinc-300 bg-white/5 rounded-lg p-3">{lead.message}</p>}
          {open && (
            <div className="flex gap-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Interní poznámka…" className="input-dark flex-1 text-sm" data-testid={`lead-note-${lead.id}`} />
              <button onClick={() => patch({ note })} disabled={saving} className="btn-accent text-sm self-start" data-testid={`lead-note-save-${lead.id}`}>Uložit</button>
            </div>
          )}
        </div>
      )}

      {aiText && (
        <div className="mt-3 bg-orange-500/5 border border-orange-500/20 rounded-lg p-3" data-testid={`lead-ai-result-${lead.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-orange-300 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Návrh odpovědi (AI)</span>
            <button onClick={() => { navigator.clipboard.writeText(aiText); toast.success("Zkopírováno."); }} className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid={`lead-ai-copy-${lead.id}`}><Copy className="w-3 h-3" /> Kopírovat</button>
          </div>
          <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{aiText}</p>
        </div>
      )}
    </div>
  );
};

const LeadsTab = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try { setLeads(await apiFetch("/admin/leads", { token })); } catch (e) { /* */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const shown = filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);
  const counts = { all: leads.length, new: leads.filter((l) => (l.status || "new") === "new").length };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div data-testid="leads-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Inbox className="w-5 h-5 text-orange-400" /> Poptávky ({leads.length})</h3>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1.5 rounded-full border ${filter === "all" ? "border-orange-500 bg-orange-500/15 text-orange-300" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid="leads-filter-all">Vše ({counts.all})</button>
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setFilter(s.key)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s.key ? s.cls : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`leads-filter-${s.key}`}>{s.label}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="leads-empty">Žádné poptávky v této kategorii.</div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {shown.map((l) => <LeadCard key={`${l.source}-${l.id}`} lead={l} token={token} onChange={load} />)}
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
